import React, { useEffect, useMemo, useState } from 'react';
import Datatable from '../../components/Datatable';
import {
    Alert, Box, Button, Checkbox, Chip, Collapse, FormControlLabel, IconButton, Paper, Tab, Tabs, TextField, Tooltip, Typography,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { red, green } from '@mui/material/colors';
import { ThemeProvider } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import rdnsTheme from './rdnsTheme';
import api, { create, update, delete_one } from '../../services/api';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import { useAuth } from '../../components/AuthProvider';
import { useLoading } from '../../components/LoadingProvider';
import { handleQueuedResponse } from '../../utils/handleQueuedResponse';

// Backend: app/endpoints/oceanide/rdns.py.
// Tudo começa pelo bloco do provedor; o resto vive dentro dele:
//   Bloco        → a faixa do provedor (/20, /22, /32 v6): cria as zonas e,
//                  com padrão, dá nome a todo IP ({ip}.sufixo)
//   Range IPv4   → um pedaço do bloco com outro padrão; vence o do bloco
//   IP específico→ um IP com nome próprio; vence range e bloco
//   IPv6         → um endereço v6 com nome próprio (v6 não expande em range)

function handlePoolResponse(res, notify, successMessage) {
    const data = res?.data || {}
    if (!data.error && data.status === 'no_reverse_nodes') {
        notify.setNotify({ open: true, severity: 'warning',
            message: `${successMessage} Nenhum servidor reverso ativo — cadastre um em Servidores DNS pra sincronizar.` })
        return { ok: true, queued: false }
    }
    return handleQueuedResponse(res, notify, { successMessage: `${successMessage} Sync na fila.` })
}
const errMsg = (e) => e.response?.data?.detail || e.response?.data?.error || e.message
const dashed = (ip) => ip.replace(/\./g, '-').replace(/:/g, '-')
const isV6 = (ip) => ip.includes(':')

export default function RDNS() {
    const auth = useAuth()
    const notify = useNotify()
    const navigate = useNavigate()
    const [tab, setTab] = useState(0)
    const [zones, setZones] = useState(false)

    useEffect(() => { document.title = "IPV7 Tools - DNS Reverso (RDNS)" }, [])

    const syncAll = () => Swal.fire({
        title: 'Sincronizar servidores reversos?',
        text: 'Regenera o local_zone.conf (blocos, ranges, IPs específicos e IPv6) e aplica em todos os servidores reversos ativos.',
        icon: 'warning', showCancelButton: true,
        confirmButtonText: 'Sim, sincronizar', cancelButtonText: 'Cancelar', confirmButtonColor: '#e01b29',
    }).then((r) => {
        if (!r.isConfirmed) return
        api.post('/dns/rdns/sync/all')
            .then(res => handleQueuedResponse(res, notify, { successMessage: 'Sync dos reversos na fila' }))
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
    })

    return (
        <ThemeProvider theme={rdnsTheme}>
        <Box sx={{ p: 2 }}>
            <ZonesDialog open={zones} onClose={() => setZones(false)} />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                <Box>
                    <Typography variant="h5">DNS Reverso (RDNS)</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Quem responde "qual o nome deste IP?" nos servidores reversos.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button size="small" variant="outlined" startIcon={<HelpOutlineIcon />} onClick={() => navigate('/dns_rdns/docs')}>Ajuda</Button>
                    <Button size="small" variant="outlined" startIcon={<ListAltIcon />} onClick={() => setZones(true)}>Zonas geradas</Button>
                    <Button size="small" variant="contained" startIcon={<SyncIcon />} disabled={!auth.hasPerm("dns_rdns.update")} onClick={syncAll}>Sincronizar servidores</Button>
                </Box>
            </Box>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab label="Blocos" />
                <Tab label="Ranges IPv4" />
                <Tab label="IPs específicos" />
                <Tab label="IPv6" />
            </Tabs>

            {tab === 0 && <BlocksTab />}
            {tab === 1 && <RangesTab />}
            {tab === 2 && <SpecificTab version={4} />}
            {tab === 3 && <SpecificTab version={6} />}
        </Box>
        </ThemeProvider>
    )
}

// ================================================================ Blocos

// Cabeçalho de cada aba: texto de ajuda à esquerda, "Adicionar" à direita.
function TabHeader({ help, action }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 860, flex: 1 }}>{help}</Typography>
            {action}
        </Box>
    )
}

const SOA_KEYS = ['ttl', 'refresh', 'retry', 'expire', 'minimum']
const SOA_LABEL = { ttl: 'TTL do SOA e NS', refresh: 'Refresh', retry: 'Retry', expire: 'Expire', minimum: 'Minimum' }
const emptyBlock = { prefix: '', suffix: '', description: '', with_zone: true, soa: {} }

function BlocksTab() {
    const auth = useAuth()
    const notify = useNotify()
    const { setLoading } = useLoading()
    const [rows, setRows] = useState([])
    const [form, setForm] = useState(emptyBlock)
    const [open, setOpen] = useState(false)
    const [editing, setEditing] = useState(null)       // bloco em edição (o prefixo não muda)
    const [soaOpen, setSoaOpen] = useState(false)
    const [soaDefaults, setSoaDefaults] = useState({})

    const load = () => api.get('/dns/rdns/blocks/all')
        .then(res => { setRows(res.data?.data || []); setSoaDefaults(res.data?.soa_defaults || {}) })
        .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
    useEffect(() => { load() }, [])

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
    const close = () => { setOpen(false); setForm(emptyBlock); setEditing(null); setSoaOpen(false) }
    const openNew = () => { setForm(emptyBlock); setEditing(null); setSoaOpen(false); setOpen(true) }
    const edit = (b) => {
        const soa = Object.fromEntries(SOA_KEYS.map(k => [k, b[`soa_${k}`] ?? '']))
        setForm({ prefix: b.prefix, suffix: suffixOf(b.template), description: b.description || '', with_zone: b.with_zone !== false, soa })
        setEditing(b.id)
        setSoaOpen(SOA_KEYS.some(k => b[`soa_${k}`]))
        setOpen(true)
    }
    const v6 = isV6(form.prefix)

    const preview = useMemo(() => {
        const [ip, len] = form.prefix.split('/')
        const n = Number(len)
        if (!ip || !n || v6) return null
        const zones = n >= 16 && n <= 24 ? 2 ** (24 - n) : null
        const first = ip.split('.').length === 4 && form.suffix ? `${dashed(ip)}.${form.suffix.replace(/\.$/, '')}.` : null
        return { zones, count: n >= 16 && n <= 24 ? 2 ** (32 - n) : null, first }
    }, [form.prefix, form.suffix, v6])

    const submit = () => {
        const suffix = form.suffix.trim().replace(/^\{ip\}\./, '').replace(/\.$/, '')
        const body = { prefix: form.prefix.trim(), template: suffix ? `{ip}.${suffix}.` : null, description: form.description || null,
                       with_zone: form.with_zone,
                       ...Object.fromEntries(SOA_KEYS.map(k => [`soa_${k}`, form.soa[k] === '' || form.soa[k] == null ? null : Number(form.soa[k])])) }
        const p = editing ? api.put(`/dns/rdns/blocks/${editing}`, body) : api.post('/dns/rdns/blocks/create', body)
        setLoading(true)
        p.then(res => { if (handlePoolResponse(res, notify, editing ? 'Bloco atualizado.' : 'Bloco criado.').ok) { close(); load() } })
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
            .finally(() => setLoading(false))
    }

    const remove = (b) => Swal.fire({
        title: `Excluir o bloco ${b.prefix}?`,
        html: `Sai junto tudo que está nele: <b>${b.zone_count} zona(s)</b>, <b>${b.ranges.length} range(s)</b> e
               <b>${b.override_count} IP(s) específico(s)</b>. As zonas saem dos servidores no próximo sync.`,
        icon: 'warning', showCancelButton: true,
        confirmButtonText: 'Sim, excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#e01b29',
    }).then((r) => {
        if (!r.isConfirmed) return
        setLoading(true)
        api.delete(`/dns/rdns/blocks/${b.id}`)
            .then(res => { if (handlePoolResponse(res, notify, 'Bloco removido.').ok) load() })
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
            .finally(() => setLoading(false))
    })

    return (
        <>
            <TabHeader
                help={<>O <b>bloco</b> é a faixa de IPs do provedor — o <code>/22</code>, o <code>/20</code>, o <code>/32</code> de IPv6. Tudo começa
                    por ele. Com <b>padrão</b>, todo IP do bloco ganha nome; ranges e IPs específicos dentro dele vencem o padrão.</>}
                action={<Button variant="contained" startIcon={<AddIcon />} disabled={!auth.hasPerm("dns_rdns.create")} onClick={openNew}>Adicionar bloco</Button>} />

            <Paper variant="outlined">
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>Bloco</TableCell><TableCell>Padrão</TableCell><TableCell>Zonas</TableCell>
                        <TableCell>Dentro dele</TableCell><TableCell>Descrição</TableCell><TableCell />
                    </TableRow></TableHead>
                    <TableBody>
                        {rows.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>
                            Nenhum bloco. Clique em <b>Adicionar bloco</b>.
                        </TableCell></TableRow>}
                        {rows.map(b => (
                            <TableRow key={b.id}>
                                <TableCell><code>{b.prefix}</code></TableCell>
                                <TableCell>{b.template
                                    ? <><code>{b.template.replace('{ip}', '<ip>')}</code>
                                        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>ex.: {b.sample}</Typography></>
                                    : <Typography variant="caption" sx={{ color: 'text.secondary' }}>sem padrão — só ranges e IPs específicos</Typography>}
                                </TableCell>
                                <TableCell>{b.with_zone === false
                                    ? <Tooltip title="Sem zona /24 (SOA/NS): só o PTR de cada IP"><Typography variant="caption" sx={{ color: 'text.secondary' }}>só PTR por IP</Typography></Tooltip>
                                    : <>{b.zone_count}{SOA_KEYS.some(k => b[`soa_${k}`]) &&
                                        <Chip size="small" variant="outlined" label="SOA próprio" sx={{ ml: 1 }} />}</>}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                        {b.ranges.map(r => <Tooltip key={r.id} title={r.template}><Chip size="small" variant="outlined" label={`range ${r.prefix}`} /></Tooltip>)}
                                        {b.override_count > 0 && <Chip size="small" variant="outlined" label={`${b.override_count} IP(s) específico(s)`} />}
                                        {b.ranges.length === 0 && b.override_count === 0 && <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>}
                                    </Box>
                                </TableCell>
                                <TableCell>{b.description}</TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Editar"><span><IconButton size="small" disabled={!auth.hasPerm("dns_rdns.update")} onClick={() => edit(b)}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                                    <Tooltip title="Excluir o bloco e tudo dentro dele"><span><IconButton size="small" disabled={!auth.hasPerm("dns_rdns.delete")} onClick={() => remove(b)}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? `Editar bloco ${form.prefix}` : 'Novo bloco'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField size="small" label="Bloco (CIDR)" value={form.prefix} onChange={set('prefix')} placeholder="10.20.32.0/20"
                            disabled={!!editing} autoFocus={!editing}
                            helperText={editing ? 'O prefixo não muda — pra outro tamanho, exclua e cadastre de novo.' : 'IPv4 de /16 a /24 · IPv6 de /16 a /64'} />
                        <TextField size="small" label="Padrão do bloco (sufixo)" value={form.suffix} onChange={set('suffix')} placeholder="exemplo.net.br"
                            disabled={v6}
                            helperText={v6 ? 'IPv6 não tem padrão — cadastre os endereços na aba IPv6.' : 'Opcional. O que vem depois do IP no nome.'} />
                        <TextField size="small" label="Descrição" value={form.description} onChange={set('description')} placeholder="Alocação principal" />
                        <FormControlLabel
                            control={<Checkbox size="small" checked={form.with_zone} onChange={e => setForm({ ...form, with_zone: e.target.checked })} />}
                            label={<Typography variant="body2">
                                {v6 ? 'Criar a zona do bloco' : 'Criar a zona de cada /24'} (SOA e NS) — responde a consulta ao bloco.
                                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    O PTR de cada IP (a consulta específica) é gerado sempre. Desmarcado, vai só ele.
                                </Typography>
                            </Typography>} />
                        {form.with_zone && <Box>
                            <Button size="small" onClick={() => setSoaOpen(!soaOpen)}>{soaOpen ? 'esconder SOA' : 'SOA (avançado)'}</Button>
                            <Collapse in={soaOpen}>
                                <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mb: 1 }}>
                                    Em segundos. Vazio = o padrão (o que aparece no campo).
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1.5 }}>
                                    {SOA_KEYS.map(k => (
                                        <TextField key={k} size="small" type="number" label={SOA_LABEL[k]} value={form.soa[k] ?? ''}
                                            placeholder={soaDefaults[k] != null ? String(soaDefaults[k]) : ''} InputLabelProps={{ shrink: true }}
                                            onChange={e => setForm({ ...form, soa: { ...form.soa, [k]: e.target.value } })} />
                                    ))}
                                </Box>
                            </Collapse>
                        </Box>}
                        {preview && preview.zones && <Typography variant="body2">
                            {form.with_zone ? <>Vai criar <b>{preview.zones}</b> zona(s) /24</> : <>Sem zona /24</>}
                            {preview.first ? <> e dar nome a <b>{preview.count}</b> IPs. Primeiro: <code>{preview.first}</code></> : '.'}
                        </Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancelar</Button>
                    <Button variant="contained" onClick={submit} disabled={!auth.hasPerm(editing ? "dns_rdns.update" : "dns_rdns.create") || !form.prefix}>
                        {editing ? 'Salvar' : 'Adicionar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

// ================================================================ Ranges

const emptyRange = { prefix: '', suffix: '', description: '', forward_zone: '', ttl: '' }
const suffixOf = (template) => (template || '').replace(/^\{ip\}\./, '').replace(/\.$/, '')

function RangesTab() {
    const auth = useAuth()
    const notify = useNotify()
    const { setLoading } = useLoading()
    const [form, setForm] = useState(emptyRange)
    const [open, setOpen] = useState(false)
    const [editing, setEditing] = useState(null)      // id em edição
    const [advanced, setAdvanced] = useState(false)
    const [refresh, setRefresh] = useState(false)
    const [test, setTest] = useState(null)

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value })
    const close = () => { setOpen(false); setForm(emptyRange); setEditing(null); setAdvanced(false) }
    const openNew = () => { setForm(emptyRange); setEditing(null); setAdvanced(false); setOpen(true) }

    // preview do que o range gera
    const preview = useMemo(() => {
        const [ip, len] = form.prefix.split('/')
        const n = Number(len)
        if (!ip || !n || isV6(ip) || !form.suffix) return null
        const count = n >= 16 && n <= 32 ? 2 ** (32 - n) : null
        const first = ip.split('.').length === 4 ? ip : null
        return { count, first: first && `${dashed(first)}.${form.suffix.replace(/\.$/, '')}.` }
    }, [form.prefix, form.suffix])

    const submit = () => {
        const body = {
            prefix: form.prefix.trim(),
            template: `{ip}.${form.suffix.trim().replace(/\.$/, '')}.`,
            description: form.description || null,
            forward_zone: form.forward_zone || null,
            ttl: form.ttl === '' ? null : Number(form.ttl),
        }
        const p = editing ? update('/dns/rdns', editing, body) : create('/dns/rdns/create', body)
        setLoading(true)
        p.then(res => { if (handlePoolResponse(res, notify, editing ? 'Range atualizado.' : 'Range criado.').ok) { close(); setRefresh(true) } })
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
            .finally(() => setLoading(false))
    }

    const edit = (id) => api.get(`/dns/rdns/${id}/summary`).then(res => {
        const d = res.data?.data || {}
        setForm({ prefix: d.prefix || '', suffix: suffixOf(d.template), description: d.description || '',
                  forward_zone: d.forward_zone_name || '', ttl: d.ttl ?? '' })
        setAdvanced(!!(d.forward_zone_name || d.ttl))
        setEditing(id)
        setOpen(true)
    }).catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))

    const remove = (id, prefix) => Swal.fire({
        title: 'Excluir range?',
        text: `Os nomes gerados por ${prefix} saem das zonas no próximo sync. IPs específicos dentro dele ficam.`,
        icon: 'warning', showCancelButton: true,
        confirmButtonText: 'Sim, excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#e01b29',
    }).then((r) => {
        if (!r.isConfirmed) return
        setLoading(true)
        delete_one('/dns/rdns', id)
            .then(res => handlePoolResponse(res, notify, 'Range removido.'))
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
            .finally(() => { setLoading(false); setRefresh(true) })
    })

    return (
        <>
            <TestDialog pool={test} onClose={() => setTest(null)} />
            <TabHeader
                help={<>Um <b>range</b> é um pedaço de um <b>bloco</b> com outro padrão de nome. Ex.: dentro do bloco <code>10.10.32.0/22</code>,
                    o range <code>10.10.34.0/24</code> + sufixo <code>static.exemplo.net.br</code> → <code>10-10-34-0.static.exemplo.net.br</code>.
                    O range vence o padrão do bloco; range menor vence o maior; um <b>IP específico</b> vence qualquer range.</>}
                action={<Button variant="contained" startIcon={<AddIcon />} disabled={!auth.hasPerm("dns_rdns.create")} onClick={openNew}>Adicionar range</Button>} />

            <Datatable
                refresh={refresh}
                handlerRefresh={() => setRefresh(!refresh)}
                title='Ranges cadastrados'
                endpoint='/dns/rdns'
                options={{ customToolbar: () => null, customToolbarSelect: () => "" }}
                columns={[
                    { name: 'id', label: 'ID', options: { display: false } },
                    { name: 'prefix', label: 'Range', options: { customBodyRender: (v) => <code>{v}</code> } },
                    { name: 'template', label: 'Nome gerado', options: { customBodyRender: (v) => <code>{(v || '').replace('{ip}', '<ip>')}</code> } },
                    { name: 'description', label: 'Descrição' },
                    {
                        name: 'actions', label: 'Ações',
                        options: {
                            filter: false, sort: false,
                            customBodyRender: (value, tableMeta) => {
                                const id = tableMeta.rowData[0], prefix = tableMeta.rowData[1], template = tableMeta.rowData[2]
                                return <>
                                    <Tooltip title="Editar"><IconButton disabled={!auth.hasPerm("dns_rdns.update")} onClick={() => edit(id)}><EditIcon /></IconButton></Tooltip>
                                    <Tooltip title="Testar nos servidores reversos"><IconButton disabled={!auth.hasPerm("dns_rdns.get_by_id")} onClick={() => setTest({ id, prefix, template })}><PlaylistAddCheckIcon /></IconButton></Tooltip>
                                    <Tooltip title="Excluir"><IconButton disabled={!auth.hasPerm("dns_rdns.delete")} onClick={() => remove(id, prefix)}><DeleteIcon /></IconButton></Tooltip>
                                </>
                            }
                        }
                    },
                ]}
            />

            <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? `Editar range ${form.prefix}` : 'Novo range'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField size="small" label="Range (CIDR IPv4)" value={form.prefix} onChange={set('prefix')} placeholder="10.10.34.0/24"
                            autoFocus={!editing} helperText="Dentro de um bloco cadastrado. IPv6 vai na aba IPv6." />
                        <TextField size="small" label="Sufixo do nome" value={form.suffix} onChange={set('suffix')} placeholder="static.exemplo.net.br"
                            helperText="O que vem depois do IP no nome." />
                        <TextField size="small" label="Descrição" value={form.description} onChange={set('description')} placeholder="IPs fixos" />
                        {preview && <Typography variant="body2">
                            Vai gerar <b>{preview.count ?? '?'}</b> nomes. Primeiro: <code>{preview.first}</code>
                        </Typography>}
                        <Box>
                            <Button size="small" onClick={() => setAdvanced(!advanced)}>{advanced ? 'esconder avançado' : 'avançado (FCrDNS, TTL)'}</Button>
                            <Collapse in={advanced}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                                    <TextField size="small" label="Zona forward (FCrDNS)" value={form.forward_zone} onChange={set('forward_zone')} placeholder="static.exemplo.net.br"
                                        helperText="Gera também o A do nome apontando pro IP. A zona precisa estar delegada pros servidores reversos." />
                                    <TextField size="small" label="TTL do PTR" type="number" value={form.ttl} onChange={set('ttl')} sx={{ width: 200 }}
                                        helperText="Em segundos. Vazio = sem TTL na linha (o Unbound usa 3600)." />
                                </Box>
                            </Collapse>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancelar</Button>
                    <Button variant="contained" onClick={submit} disabled={!auth.hasPerm(editing ? "dns_rdns.update" : "dns_rdns.create") || !form.prefix || !form.suffix}>
                        {editing ? 'Salvar' : 'Adicionar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

// ================================================================ IP específico / IPv6

const TEXT = {
    4: {
        add: 'Adicionar IP específico',
        dialog: 'IP específico',
        help: <>Um <b>IP com nome próprio</b> — o servidor de e-mail de um cliente, um roteador, um host que não deve seguir o padrão.
            O IP precisa estar dentro de um <b>bloco</b> cadastrado. Este nome <b>vence</b> o do range e o padrão do bloco (a lista mostra qual seria).</>,
        placeholder: '10.10.32.71',
        list: 'IPs específicos cadastrados',
    },
    6: {
        add: 'Adicionar endereço IPv6',
        dialog: 'Endereço IPv6',
        help: <>IPv6 <b>não entra em range</b>: cada endereço que precisa de nome reverso é cadastrado um a um (infra, peering, clientes
            estáticos). O endereço precisa estar dentro de um <b>bloco IPv6</b> cadastrado (ex.: o /32 do provedor) e entra na zona dele.</>,
        placeholder: '2001:db8:10:e1::2',
        list: 'Endereços IPv6 cadastrados',
    },
}

function SpecificTab({ version }) {
    const auth = useAuth()
    const notify = useNotify()
    const { setLoading } = useLoading()
    const [rows, setRows] = useState([])
    const [form, setForm] = useState({ ip: '', name: '' })
    const [open, setOpen] = useState(false)
    const [editing, setEditing] = useState(false)     // editando: o IP não muda, só o nome
    const t = TEXT[version]

    const load = () => api.get('/dns/rdns/overrides/all')
        .then(res => setRows((res.data?.data || []).filter(r => r.version === version)))
        .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
    useEffect(() => { load() }, [version])

    const close = () => { setOpen(false); setForm({ ip: '', name: '' }); setEditing(false) }
    const openNew = () => { setForm({ ip: '', name: '' }); setEditing(false); setOpen(true) }
    const edit = (r) => { setForm({ ip: r.ip, name: (r.name || '').replace(/\.$/, '') }); setEditing(true); setOpen(true) }
    const wrongFamily = form.ip && (isV6(form.ip) !== (version === 6))

    // o create faz upsert pelo IP: serve pra adicionar e pra trocar o nome
    const save = () => {
        setLoading(true)
        api.post('/dns/rdns/overrides/create', { ip: form.ip.trim(), name: form.name.trim() })
            .then(res => { if (handlePoolResponse(res, notify, 'Nome salvo.').ok) { close(); load() } })
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
            .finally(() => setLoading(false))
    }
    const remove = (r) => Swal.fire({
        title: 'Remover nome?', text: `${r.ip} volta a ${r.pool_name ? `seguir o padrão (${r.pool_name})` : 'não ter nome reverso'}.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'Sim, remover', cancelButtonText: 'Cancelar', confirmButtonColor: '#e01b29',
    }).then(res => {
        if (!res.isConfirmed) return
        api.delete(`/dns/rdns/overrides/${r.id}`)
            .then(res2 => { if (handlePoolResponse(res2, notify, 'Nome removido.').ok) load() })
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
    })

    return (
        <>
            <TabHeader help={t.help}
                action={<Button variant="contained" startIcon={<AddIcon />} disabled={!auth.hasPerm("dns_rdns.update")} onClick={openNew}>{t.add}</Button>} />

            <Paper variant="outlined">
                <Typography variant="subtitle2" sx={{ px: 2, pt: 2 }}>{t.list} ({rows.length})</Typography>
                <Table size="small">
                    <TableHead><TableRow>
                        <TableCell>IP</TableCell><TableCell>Nome</TableCell>
                        <TableCell>{version === 6 ? 'Bloco / zona' : 'Nome que ele vence'}</TableCell><TableCell />
                    </TableRow></TableHead>
                    <TableBody>
                        {rows.length === 0 && <TableRow><TableCell colSpan={4} sx={{ color: 'text.secondary' }}>Nenhum cadastrado.</TableCell></TableRow>}
                        {rows.map(r => (
                            <TableRow key={r.id}>
                                <TableCell><code>{r.ip}</code></TableCell>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>
                                    {version === 6 ? <><code>{r.block_prefix || '—'}</code> <Typography component="span" variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>{r.zone}</Typography></>
                                        : r.pool_name
                                            ? <><code>{r.pool_prefix || r.block_prefix}</code> <Typography component="span" variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', ml: 1 }}>{r.pool_name}</Typography></>
                                            : <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>nenhum — bloco <code>{r.block_prefix || '—'}</code> sem padrão</Typography>}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Tooltip title="Editar nome"><span><IconButton size="small" disabled={!auth.hasPerm("dns_rdns.update")} onClick={() => edit(r)}><EditIcon fontSize="small" /></IconButton></span></Tooltip>
                                    <Tooltip title="Remover"><span><IconButton size="small" disabled={!auth.hasPerm("dns_rdns.update")} onClick={() => remove(r)}><DeleteIcon fontSize="small" /></IconButton></span></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? `Editar ${form.ip}` : `Novo — ${t.dialog}`}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField size="small" label={version === 6 ? 'Endereço IPv6' : 'IP'} value={form.ip} onChange={e => setForm({ ...form, ip: e.target.value })}
                            placeholder={t.placeholder} disabled={editing} autoFocus={!editing} error={!!wrongFamily}
                            helperText={wrongFamily ? (version === 6 ? 'Isso é IPv4 — use a aba "IPs específicos".' : 'Isso é IPv6 — use a aba "IPv6".') : ' '} />
                        <TextField size="small" label="Nome (FQDN)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                            placeholder="mail.cliente.com.br" autoFocus={editing} helperText="O nome que o PTR vai responder." />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={close}>Cancelar</Button>
                    <Button variant="contained" onClick={save} disabled={!auth.hasPerm("dns_rdns.update") || !form.ip || !form.name || wrongFamily}>
                        {editing ? 'Salvar' : 'Adicionar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

// ================================================================ diálogos (teste, zonas)

function TestDialog({ pool, onClose }) {
    const notify = useNotify()
    const [result, setResult] = useState(null)
    const [running, setRunning] = useState(false)
    useEffect(() => {
        setResult(null)
        if (!pool) return
        setRunning(true)
        api.post(`/dns/rdns/${pool.id}/test`)
            .then(res => { if (res.data?.error) notify.setNotify({ open: true, message: res.data.error, severity: 'error' }); else setResult(res.data) })
            .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
            .finally(() => setRunning(false))
    }, [pool?.id])
    return (
        <Dialog open={!!pool} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Teste — <code>{pool?.prefix}</code></DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Consulta o PTR de verdade em cada servidor reverso: primeiro, último, 3 aleatórios e todos os IPs específicos do range.
                </Typography>
                {running && <Typography>Consultando…</Typography>}
                {result && <>
                    <Chip size="small" sx={{ mb: 2 }} color={result.ok === result.total ? 'success' : 'warning'} label={`${result.ok}/${result.total} respostas batem`} />
                    {result.ok < result.total && result.results.some(r => r.nodes.some(n => !n.match && n.got?.length)) && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            O servidor <b>respondeu outro nome</b> (ou dois) em vez do esperado. Quase sempre é a mesma zona
                            escrita também em outro arquivo do servidor (normalmente o <code>unbound.conf</code>) — o Unbound junta
                            as duas. Apague essas linhas de lá e recarregue.
                        </Alert>
                    )}
                    {result.ok < result.total && !result.results.some(r => r.nodes.some(n => n.got?.length)) && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            O servidor <b>não respondeu nenhum nome</b> (NXDOMAIN/timeout). Ou o sync ainda não rodou nele
                            (veja a fila), ou a zona não está sendo incluída — confira o <code>include</code> do <code>zones.conf</code>.
                        </Alert>
                    )}
                    <Table size="small">
                        <TableHead><TableRow><TableCell>IP</TableCell><TableCell>Esperado</TableCell><TableCell>Servidor</TableCell><TableCell>Respondeu</TableCell><TableCell /></TableRow></TableHead>
                        <TableBody>
                            {result.results.map(r => r.nodes.map((n, i) => (
                                <TableRow key={`${r.ip}-${n.node_id}`}>
                                    <TableCell>{i === 0 && <code>{r.ip}</code>}</TableCell>
                                    <TableCell>{i === 0 && (r.expected || <em>sem PTR</em>)}</TableCell>
                                    <TableCell>{n.name}</TableCell>
                                    <TableCell>{n.got?.length ? n.got.join(', ') : <em>{n.status}{n.error ? ` — ${n.error}` : ''}</em>}</TableCell>
                                    <TableCell>{n.match ? <CheckIcon sx={{ color: green[500] }} /> : <CloseIcon sx={{ color: red[500] }} />}</TableCell>
                                </TableRow>
                            )))}
                        </TableBody>
                    </Table>
                </>}
            </DialogContent>
            <DialogActions><Button onClick={onClose} variant="outlined">Fechar</Button></DialogActions>
        </Dialog>
    )
}

function ZonesDialog({ open, onClose }) {
    const notify = useNotify()
    const [zones, setZones] = useState([])
    const [file, setFile] = useState(null)
    useEffect(() => {
        if (!open) return
        setFile(null)
        api.get('/dns/rdns/zones/all').then(res => setZones(res.data?.data || [])).catch(console.log)
    }, [open])
    const show = (z) => api.get(`/dns/rdns/zones/${z.id}/zonefile`)
        .then(res => typeof res.data === 'string' ? setFile({ zone: z, text: res.data }) : notify.setNotify({ open: true, message: res.data?.error || 'Erro', severity: 'error' }))
        .catch(e => notify.setNotify({ open: true, message: errMsg(e), severity: 'error' }))
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Zonas geradas</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                    O que os servidores reversos recebem no <code>local_zone.conf</code>: um trecho por zona (<code>local-zone</code> + SOA + NS +
                    <code>local-data-ptr</code>). As zonas derivam dos blocos — ninguém digita <code>in-addr.arpa</code>.
                </Typography>
                {!file && <Table size="small">
                    <TableHead><TableRow><TableCell>Zona</TableCell><TableCell>Bloco</TableCell><TableCell>Tipo</TableCell><TableCell>Serial</TableCell><TableCell>Servidores</TableCell><TableCell /></TableRow></TableHead>
                    <TableBody>
                        {zones.length === 0 && <TableRow><TableCell colSpan={6} sx={{ color: 'text.secondary' }}>Nenhuma zona ainda.</TableCell></TableRow>}
                        {zones.map(z => (
                            <TableRow key={z.id}>
                                <TableCell><code>{z.name}</code></TableCell>
                                <TableCell>{z.block ? <code>{z.block}</code> : '—'}</TableCell>
                                <TableCell><Chip size="small" variant="outlined" label={z.kind === 'reverse' ? 'reversa' : 'forward'} /></TableCell>
                                <TableCell>{z.serial || '—'}</TableCell>
                                <TableCell>{z.nodes.join(', ') || <em>nenhum</em>}</TableCell>
                                <TableCell align="right"><Button size="small" onClick={() => show(z)}>ver trecho</Button></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>}
                {file && <>
                    <Button size="small" onClick={() => setFile(null)} sx={{ mb: 1 }}>← voltar</Button>
                    <Box component="pre" sx={{ fontSize: 12, bgcolor: '#1b2a2e', color: '#dce7e8', p: 2, borderRadius: 1, overflowX: 'auto', maxHeight: 480 }}>{file.text}</Box>
                </>}
            </DialogContent>
            <DialogActions><Button onClick={onClose} variant="outlined">Fechar</Button></DialogActions>
        </Dialog>
    )
}
